import toast from "react-hot-toast";
import { database as db } from "../../index";
import { collection, doc, getDocs, deleteDoc, setDoc, addDoc, serverTimestamp } from "firebase/firestore";
import isAdmin from "./isAdmin";

export default class Resources {
  
  /**
   * Creates a new resource category.
   * @author Lewis Harman
   * @version 1.0.0
   * @since 2025-04-07
   * @param {string} categoryName The name of the new category.
   * @param {string} emailAddress The email address of the current user.
   * @returns {Promise<string | null>} The id of the new category.
   * @example
   *  >>> async function main() {
   *  ...   const resources = new Resources();
   *  ...   const existingCategoryName = "Test Resources";
   *  ...   const newCategoryId = await resources.addCategory(existingCategoryName);
   *  ...   console.log(newCategoryId);
   *  ... }
   *  ... main();
   */
  async addCategory(categoryName, emailAddress) {

    if (!isAdmin(emailAddress)) {
      toast.error("You must be an admin to delete a resource category.");
      return false;
    }

    const loadingId = toast.loading("Adding resource category...");

    try {
      const categoriesRef = collection(db, "resources");
      const categoryRef = doc(categoriesRef, categoryName);
      await setDoc(categoryRef, {
        name: categoryName
      }, {
        merge: false
      });
      toast.success(`Created the ${categoryName} resource category successfully.`);
      return(categoryName);

    } catch (error) {
      toast.error(`Unable to create the ${categoryName} resource category. Please refresh the page and try again.`);
      return;

    } finally {
      toast.remove(loadingId);
    }

  }

  /**
   * Creates a new resource in a resource category.
   * @author Lewis Harman
   * @version 1.0.0
   * @since 2025-04-07
   * @param {string} categoryName The name of an existing category.
   * @param {string} resourceName The name of the new resource.
   * @param {string} resourceLink The URL to the new resource.s
   * @param {string} emailAddress The email address of the current user.
   * @returns {Promise<string | null>} The id of the new resource. Null if unsuccessful.
   * @example
   *  >>> async function main() {
   *  ...   const resources = new Resources();
   *  ...   const existingCategoryName = "Test Resources"
   *  ...   const newResourceName = "My Document";
   *  ...   const newResourceUrl = "https://mywebsite.com/my-document.pdf"
   *  ...   const newResourceId = await resources.addResource(
   *  ...     existingCategoryName,
   *  ...     newResourceName,
   *  ...     newResourceUrl
   *  ...   );
   *  ...   console.log(newResourceId);
   *  ... }
   *  ... main();
   */
  async addResource(categoryName, resourceName, resourceLink, emailAddress) {

    if (!isAdmin(emailAddress)) {
      toast.error("You must be an admin to add a resource.");
      return false;
    }

    const loadingId = toast.loading("Adding resource...");

    try {
      const categoriesRef = collection(db, "resources");
      const categoryRef = doc(categoriesRef, categoryName)
      const resourcesRef = collection(categoryRef, "items");
      const newResourceSnap = await addDoc(resourcesRef, {
        name: resourceName,
        link: resourceLink,
        createdAt: serverTimestamp()
      }, {
        merge: false
      });
      toast.success(`Created the ${resourceName} resource for the ${categoryName} resource category successfully.`);
      return(newResourceSnap.id);

    } catch (error) {
      toast.error(`Unable to create the ${resourceName} resource for the ${categoryName} resource category. Please refresh the page and try again.`);
      return;

    } finally {
      toast.remove(loadingId);
    }

  }

  /**
   * Fetches all existing categories.
   * @author Lewis Harman
   * @version 1.0.0
   * @since 2025-04-07
   * @returns {Promise<string[] | null>} An array of category ids. Null if unsuccessful.
   * @example
   *  >>> async function main() {
   *  ...   const resources = new Resources();
   *  ...   const categoryIds = await resources.getCategories();
   *  ...   console.log(categoryIds);
   *  ... }
   *  ... main();
   */
  async getCategories() {

    const loadingId = toast.loading("Loading resources...");

    try {
      const categoriesRef = collection(db, "resources");
      const categoriesSnap = await getDocs(categoriesRef);
      return categoriesSnap.docs.map((category) => category.id);

    } catch (error) {
      console.error(error);
      toast.error(`Unable to fetch the resource categories. Please refresh the page and try again.`);
      return;

    } finally {
      toast.remove(loadingId);
    }

  }

  /**
   * Fetches all existing resources from all existing categories.
   * @author Lewis Harman
   * @version 1.0.0
   * @since 2025-04-07
   * @returns {Promise<{
   *  name: string,
   *  resources: {
   *    id: string,
   *    name: string,
   *    link: string,
   *    createdAt: Date || null
   *  }[]
   * }[] | null>} An array of items grouped by category.
   * @example
   *  >>> async function main() {
   *  ...   const resources = new Resources();
   *  ...   const resourcesByCategory = await resources.getResources();
   *  ...   console.log(resourcesByCategory);
   *  ... }
   *  ... main();
   */
  async getResources() {

    const loadingId = toast.loading("Loading resources...");

    try {
      const resources = [];
      const categoriesRef = collection(db, "resources");
      const categoriesSnap = await getDocs(categoriesRef);
      for (const categorySnap of categoriesSnap.docs) {
        const categoryName = categorySnap.id;
        const itemsRef = collection(categorySnap.ref, "items");
        const itemsSnap = await getDocs(itemsRef);

        const items = itemsSnap.docs.map((itemSnap) => {
          const id = itemSnap.id;
          const { name, link, createdAt } = itemSnap.data();
          return({ id, name, link, createdAt: createdAt?.toDate?.() ?? null });
        });

        resources.push({
          name: categoryName,
          items
        });
      }
      return resources;

    } catch (error) {
      console.error(error);
      toast.error(`Unable to fetch the resources. Please refresh the page and try again.`);
      return;

    } finally {
      toast.remove(loadingId);
    }

  }

  /**
   * Deletes an existing category. Only for admins.
   * @author Lewis Harman
   * @version 1.0.0
   * @since 2025-04-07
   * @param {string} categoryName The name of an existing category.
   * @param {string} emailAddress The email address of the current user.
   * @returns {boolean} True if successful. False otherwise.
   * @example
   *  >>> async function main() {
   *  ...   const resources = new Resources();
   *  ...   const categoryToDelete = "My Category";
   *  ...   const isDeleted = resources.popCategory(categoryToDelete);
   *  ...   console.log(isDeleted);
   *  ... }
   *  ... main();
  */
  async popCategory(categoryName, emailAddress) {

    if (!isAdmin(emailAddress)) {
      toast.error("You must be an admin to delete a resource category.");
      return false;
    }

    const loadingId = toast.loading("Deleting resource category...");

    try {
      const categoriesRef = collection(db, "resources");
      const categoryRef = doc(categoriesRef, categoryName);
      await deleteDoc(categoryRef);
      toast.success(`Deleted the resource categories and their resources successfully.`);
      return true;
      
    } catch (error) {
      toast.error(`Unable to delete the ${categoryName} resource category. Please refresh the page and try again.`);
      return false;

    } finally {
      toast.remove(loadingId);
    }

  }

  /**
   * Deletes a resource within a resource category. Only for admins.
   * @author Lewis Harman
   * @version 1.0.0
   * @since 2025-04-07
   * @param {string} categoryName The name of an existing resource category.
   * @param {string} resourceId The given document id of an existing resource.
   * @param {string} emailAddress The email address of the current user.
   * @returns {boolean} True if successful. False otherwise.
   * @example
   *  >>> async function main() {
   *  ...   const resources = new Resources();
   *  ...   const category = "My Category"
   *  ...   const resourceToDelete = "fK3ddutEpD2qQqRMXNW5"; // not real, don't copy!!!
   *  ...   const isDeleted = resources.popResource(category, resourceToDelete);
   *  ...   console.log(isDeleted);
   *  ... }
   *  ... main();
   *  false
  */
  async popResource(categoryName, resourceId, emailAddress) {

    if (!isAdmin(emailAddress)) {
      toast.error("You must be an admin to delete a resource.");
      return false;
    }

    const loadingId = toast.loading("Deleting resource...");

    try {
      const categoriesRef = collection(db, "resources");
      const categoryRef = doc(categoriesRef, categoryName);
      const resourcesRef = collection(categoryRef, "items");
      const resourceRef = doc(resourcesRef, resourceId);
      await deleteDoc(resourceRef);
      toast.success(`Deleted the resource successfully.`);
      return true;
      
    } catch (error) {
      toast.error(`Unable to delete the ${categoryName} resource. Please refresh the page and try again.`);
      return false;

    } finally {
      toast.remove(loadingId);
    }

  }

}